import { Link } from 'react-router-dom';
import type { NavPage, SiteSettings } from '../lib/api';
import { currentYear } from '../lib/datetime';

/* Small inline icons — kept local so the footer stays self-contained. */
const iconClass = 'h-4 w-4 shrink-0 text-gold-600';

const PhoneIcon = () => (
  <svg aria-hidden viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.9">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.5 5.5A2.5 2.5 0 0 1 5 3h1.6a1 1 0 0 1 .96.73l.9 3.1a1 1 0 0 1-.4 1.1l-1.3.9a13.5 13.5 0 0 0 6.4 6.4l.9-1.3a1 1 0 0 1 1.1-.4l3.1.9a1 1 0 0 1 .73.96V19a2.5 2.5 0 0 1-2.5 2.5A16.5 16.5 0 0 1 2.5 5.5Z"
    />
  </svg>
);

const MailIcon = () => (
  <svg aria-hidden viewBox="0 0 24 24" className={iconClass} fill="none" stroke="currentColor" strokeWidth="1.9">
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 7 7.4 5.3a2 2 0 0 0 2.2 0L20.5 7" />
  </svg>
);

/*
  Warm gold fade rather than a dark slab, so the page's sand tones carry to the
  bottom edge. Menu sections plus contact details — Related Links was dropped
  from the old footer.
*/
export default function Footer({
  pages,
  settings,
}: {
  pages: NavPage[];
  settings: SiteSettings;
}) {
  const contact = settings.contact;
  const groups = [...new Set(pages.map((p) => p.navGroup).filter(Boolean))] as string[];

  return (
    <footer className="relative overflow-hidden border-t border-gold-200/70 bg-linear-to-b from-sand-50 via-gold-100/70 to-gold-200/60">
      {/* Soft light bloom so the gradient reads as warmth, not a flat band. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-gold-300/25 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-sand-100/70 blur-3xl"
      />

      <div className="relative mx-auto w-full px-4 py-11 sm:px-6 lg:px-10 lg:py-12 xl:px-14">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {groups.map((group) => (
            <nav key={group} aria-label={group}>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-gold-600">
                {group}
              </p>
              <ul className="mt-4 space-y-2.5">
                {pages
                  .filter((p) => p.navGroup === group)
                  .map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={`/${page.slug}`}
                        className="text-[0.9375rem] text-ink-600 transition-colors hover:text-ink-900"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
              </ul>
            </nav>
          ))}

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-gold-600">
              Contact Us
            </p>
            <ul className="mt-4 space-y-2.5 text-[0.9375rem] text-ink-600">
              {contact?.phone?.map((number) => (
                <li key={number}>
                  <a
                    href={`tel:${number.replace(/[^+\d]/g, '')}`}
                    className="flex items-center gap-2.5 transition-colors hover:text-ink-900"
                  >
                    <PhoneIcon />
                    {number}
                  </a>
                </li>
              ))}
              {contact?.email && (
                <li>
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2.5 font-medium text-ink-900 transition-colors hover:text-gold-600"
                  >
                    <MailIcon />
                    {contact.email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-9 flex flex-col items-center gap-2 border-t border-gold-300/50 pt-5 text-center text-sm text-ink-600 sm:flex-row sm:justify-between">
          <p>© {currentYear()} Full Time Training in Malaysia. All rights reserved.</p>
          <Link to="/privacy-policy" className="transition-colors hover:text-ink-900">
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
}
